import { useCallback, useContext, useEffect, useRef } from 'react';
import SnackBarContext from '../../context/snackbar';
import UserContext from '../../context/user';
import useContainerApiRoutes, { ContainerOutcome } from '../../hooks/api/useContainerApiRoutes';

// How often to ask while the tab is in the foreground. Outcomes arrive by
// email too, so this only needs to feel prompt, not instant.
const POLL_MS = 60_000;

const seenKey = (userId: string) => `virtualstack:lastOutcomeSeen:${userId}`;

/**
 * Tells a logged-in requester when a shipping line accepts or declines one of
 * their re-use requests, so they don't have to go and check their email.
 *
 * Renders nothing: it polls while the tab is visible and raises a toast.
 */
const OutcomeNotifier = () => {
  const {
    user,
    isLoggedIn,
    constants: { shippingLines },
  } = useContext(UserContext);
  const { showSnack } = useContext(SnackBarContext);
  const { getRecentOutcomes } = useContainerApiRoutes();
  // Guards against overlapping polls (a slow request plus a visibility change).
  const inFlight = useRef(false);

  const userId = user?._id;

  const describe = useCallback(
    (outcome: ContainerOutcome) => {
      const container = outcome.containerCode || 'Your container';
      const line =
        shippingLines.find((l) => l.code === outcome.shippingLineCode)?.name ||
        outcome.shippingLineCode ||
        'the shipping line';
      if (outcome.accepted) return `${container} was approved for re-use by ${line}.`;
      return outcome.requestDeclineReason
        ? `${container} was declined by ${line}: ${outcome.requestDeclineReason}`
        : `${container} was declined by ${line}.`;
    },
    [shippingLines]
  );

  const check = useCallback(async () => {
    if (!userId || inFlight.current || document.visibilityState !== 'visible') return;
    const key = seenKey(userId);
    const storedSince = window.localStorage.getItem(key);
    // First run on this device: start from now, so past outcomes (already
    // emailed, probably already known) don't arrive as a burst of toasts.
    if (!storedSince) {
      window.localStorage.setItem(key, new Date().toISOString());
      return;
    }

    inFlight.current = true;
    try {
      const { outcomes } = await getRecentOutcomes(storedSince);
      if (outcomes.length === 0) return;

      const newest = outcomes.reduce(
        (latest, outcome) => (outcome.decidedAt > latest ? outcome.decidedAt : latest),
        storedSince
      );
      window.localStorage.setItem(key, newest);

      if (outcomes.length === 1) {
        const [outcome] = outcomes;
        showSnack(describe(outcome), outcome.accepted ? 'success' : 'warning');
      } else {
        const approved = outcomes.filter((o) => o.accepted).length;
        const declined = outcomes.length - approved;
        const parts = [approved ? `${approved} approved` : '', declined ? `${declined} declined` : ''].filter(Boolean);
        showSnack(`${outcomes.length} re-use requests were reviewed: ${parts.join(', ')}.`, 'info');
      }
    } catch (error) {
      // Background poll: a failure here must never interrupt the user.
    } finally {
      inFlight.current = false;
    }
  }, [describe, getRecentOutcomes, showSnack, userId]);

  useEffect(() => {
    if (!isLoggedIn || !userId) return;
    check();
    const interval = window.setInterval(check, POLL_MS);
    // Catch up straight away when the tab is brought back to the front.
    const onVisible = () => {
      if (document.visibilityState === 'visible') check();
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      window.clearInterval(interval);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [check, isLoggedIn, userId]);

  return null;
};

export default OutcomeNotifier;
